import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { changePassword as requestChangePassword, getCurrentUser, signIn as requestSignIn, signOut as requestSignOut } from "../api/auth";
import type { AppRole, AuthenticatedUser } from "../api/auth";
import { ApiError } from "../api/errors";
import { frontendEnvironment } from "../api/env";
import { AUTHENTICATION_REQUIRED_EVENT } from "../api/client";

interface AuthenticationState {
  loading: boolean;
  user: AuthenticatedUser | null;
  bootstrapFailure: string | null;
  sessionNotice: string | null;
  retryBootstrap: () => void;
  clearSessionNotice: () => void;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const localDevelopmentIdentity: AuthenticatedUser = {
  id: "local-development",
  username: "local-development",
  display_name: "Local development",
  role: "ADMIN",
  must_change_password: false,
  session_expires_at: "",
};

const AuthContext = createContext<AuthenticationState | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [loading, setLoading] = useState(frontendEnvironment.authRequired);
  const [user, setUser] = useState<AuthenticatedUser | null>(
    frontendEnvironment.authRequired ? null : localDevelopmentIdentity,
  );
  const [bootstrapFailure, setBootstrapFailure] = useState<string | null>(null);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!frontendEnvironment.authRequired) return;
    const controller = new AbortController();
    const cancelledBeforeDispatch = Symbol("cancelled-before-dispatch");
    // Match the remote-data bootstrap boundary: StrictMode's development-only
    // probe is cancelled before it can issue a duplicate authentication read.
    const dispatch = async () => {
      await Promise.resolve();
      return controller.signal.aborted
        ? cancelledBeforeDispatch
        : getCurrentUser(controller.signal);
    };
    void dispatch()
      .then((response) => {
        if (response === cancelledBeforeDispatch || controller.signal.aborted) return;
        setUser(response.user);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || (error instanceof ApiError && error.kind === "cancelled")) return;
        if (error instanceof ApiError && error.kind === "http" && error.status === 401) {
          setUser(null);
          return;
        }
        // A gateway/backend readiness failure is not evidence that a valid
        // session is absent.  Keep it distinct from 401 so the shell does not
        // enter a false login loop; the user gets an explicit, bounded retry.
        setBootstrapFailure(
          error instanceof ApiError
            ? error.message
            : "MBIP session verification is temporarily unavailable.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [bootstrapAttempt]);

  useEffect(() => {
    if (!frontendEnvironment.authRequired) return;
    const expire = () => {
      setUser(null);
      setSessionNotice("Your session expired. Sign in again.");
    };
    window.addEventListener(AUTHENTICATION_REQUIRED_EVENT, expire);
    return () => window.removeEventListener(AUTHENTICATION_REQUIRED_EVENT, expire);
  }, []);

  useEffect(() => {
    if (user === null || user.session_expires_at === "") return;
    const remaining = Date.parse(user.session_expires_at) - Date.now();
    if (remaining <= 0) {
      const timeout = window.setTimeout(() => {
        setUser(null);
        setSessionNotice("Your session expired. Sign in again.");
      }, 0);
      return () => window.clearTimeout(timeout);
    }
    const timeout = window.setTimeout(() => {
      setUser(null);
      setSessionNotice("Your session expired. Sign in again.");
    }, Math.min(remaining, 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [user]);

  const signIn = useCallback(async (username: string, password: string) => {
    const response = await requestSignIn(username, password);
    setUser(response.user);
    setSessionNotice(null);
  }, []);
  const signOut = useCallback(async () => {
    if (!frontendEnvironment.authRequired) return;
    try { await requestSignOut(); } finally { setUser(null); setSessionNotice(null); }
  }, []);
  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await requestChangePassword(currentPassword, newPassword);
    setUser(null);
  }, []);
  const clearSessionNotice = useCallback(() => setSessionNotice(null), []);
  const retryBootstrap = useCallback(() => {
    setLoading(true);
    setBootstrapFailure(null);
    setBootstrapAttempt((value) => value + 1);
  }, []);
  const state = useMemo(() => ({ loading, user, bootstrapFailure, sessionNotice, retryBootstrap, clearSessionNotice, signIn, signOut, changePassword }), [loading, user, bootstrapFailure, sessionNotice, retryBootstrap, clearSessionNotice, signIn, signOut, changePassword]);
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuthentication(): AuthenticationState {
  const state = useContext(AuthContext);
  if (state === null) throw new Error("Authentication context is unavailable");
  return state;
}

export type { AppRole };
